<template>
  <div class="content-wrapper view-billing-plans">
    <div class="bp">
      <!--
        ผลการชำระเงินหลังกลับจากผู้ให้บริการ — วางบนสุดของหน้า ไม่ใช่ห้อยท้ายตารางแพ็ก
        คนที่เพิ่งกลับมาจากธนาคารมาหาคำตอบเดียวว่า "เงินตัดไหม" ไม่ได้มาเลือกแพ็กต่อ
      -->
      <section v-if="showResult" class="bp-result" :class="`bp-result--${resultTone}`" role="status" aria-live="polite">
        <div class="bp-result__icon">
          <svg v-if="resultTone === 'waiting'" width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.2" opacity="0.28" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
          </svg>
          <svg v-else-if="resultTone === 'success'" width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12.6 9.6 17.2 19 7.8" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else-if="resultTone === 'failed'" width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7.5 7.5l9 9M16.5 7.5l-9 9" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
          </svg>
          <svg v-else width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8.6" stroke="currentColor" stroke-width="2" />
            <path d="M12 7.6V12l3 1.9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <div class="bp-result__text">
          <template v-if="resultTone === 'waiting'">
            <h2 class="bp-result__title">กำลังตรวจสอบผลการชำระเงิน…</h2>
            <p class="bp-result__detail">ระบบรอการยืนยันจากผู้ให้บริการ ปกติใช้เวลาไม่เกินหนึ่งนาที</p>
          </template>
          <template v-else-if="resultTone === 'success'">
            <h2 class="bp-result__title">ชำระเงินสำเร็จ — เปิดใช้แพ็ก{{ purchasedPlanName }}แล้ว</h2>
            <p class="bp-result__detail">
              <template v-if="purchasedEndDate">ใช้งานได้ถึง {{ formatDate(purchasedEndDate) }} · </template>สิทธิ์ทั้งหมดพร้อมใช้งานทันที
            </p>
          </template>
          <template v-else-if="resultTone === 'failed'">
            <h2 class="bp-result__title">ชำระเงินไม่สำเร็จ{{ returnedOrder.failureReason ? ` — ${returnedOrder.failureReason}` : "" }}</h2>
            <p class="bp-result__detail">ยังไม่มีการเรียกเก็บเงินจากบัญชีของคุณ เลือกแพ็กใหม่เพื่อลองอีกครั้ง</p>
          </template>
          <template v-else>
            <h2 class="bp-result__title">ยังไม่ได้รับการยืนยันจากผู้ให้บริการ</h2>
            <p class="bp-result__detail">ถ้าคุณจ่ายเงินไปแล้ว สิทธิ์จะเปิดให้อัตโนมัติเมื่อได้รับการยืนยัน ไม่ต้องจ่ายซ้ำ</p>
          </template>
          <p v-if="orderNumber && resultTone !== 'success'" class="bp-result__meta">เลขคำสั่งซื้อ {{ orderNumber }}</p>
        </div>

        <div class="bp-result__side">
          <div v-if="resultTone === 'waiting'" class="bp-progress" aria-hidden="true"><span></span></div>
          <button v-else-if="resultTone === 'pending'" type="button" class="bp-btn bp-btn--ghost" @click="recheckOrder">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M13.4 8a5.4 5.4 0 1 1-1.6-3.8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              <path d="M13.6 2.4v2.8h-2.8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            ตรวจสอบอีกครั้ง
          </button>
          <button v-else type="button" class="bp-btn bp-btn--ghost" @click="dismissResult">ปิด</button>
        </div>
      </section>

      <header class="bp-head">
        <h1 class="bp-head__title">แพ็กเกจการใช้งาน</h1>
        <p class="bp-head__sub">เลือกแพ็กที่เหมาะกับสาขา เปลี่ยนหรือยกเลิกได้ทุกเมื่อ</p>
      </header>

      <!-- โครงร่างรูปทรงเดียวกับการ์ดจริง ไม่ใช่วงกลมหมุนกลางจอ — หน้าไม่กระโดดตอนข้อมูลมาถึง -->
      <div v-if="isLoading" class="bp-grid bp-grid--n3" aria-busy="true" aria-label="กำลังโหลดรายการแพ็กเกจ">
        <div v-for="n in 3" :key="n" class="bp-skeleton">
          <span class="bp-skeleton__bar" style="width: 74px; height: 15px"></span>
          <span class="bp-skeleton__bar" style="width: 148px; height: 34px"></span>
          <span class="bp-skeleton__rule"></span>
          <span class="bp-skeleton__bar" style="width: 100%"></span>
          <span class="bp-skeleton__bar" style="width: 84%"></span>
          <span class="bp-skeleton__bar" style="width: 66%"></span>
          <span class="bp-skeleton__cta"></span>
        </div>
      </div>

      <div v-else-if="loadError" class="bp-empty">
        <div class="bp-empty__icon">
          <svg width="23" height="23" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 8v5" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" />
            <circle cx="12" cy="16.4" r="1.2" fill="currentColor" />
            <circle cx="12" cy="12" r="8.8" stroke="currentColor" stroke-width="1.8" opacity="0.4" />
          </svg>
        </div>
        <h2 class="bp-empty__title">ไม่สามารถโหลดรายการแพ็กเกจได้</h2>
        <p class="bp-empty__detail">กรุณาลองใหม่อีกครั้ง หากยังไม่ได้ให้ติดต่อทีมงาน DentCloud</p>
        <button type="button" class="bp-btn bp-btn--ghost" @click="fetchPlans">ลองใหม่</button>
      </div>

      <template v-else>
        <div class="bp-current">
          <div class="bp-current__icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M3 9h18M7 3v4M17 3v4M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="bp-current__main">
            <span class="bp-current__label">แพ็กปัจจุบันของสาขา</span>
            <span class="bp-current__name">{{ currentPlanName }}</span>
          </div>
          <div class="bp-current__side">
            <span class="bp-pill" :class="expiresSoon ? 'bp-pill--warn' : 'bp-pill--calm'">
              <template v-if="daysLeft !== null">เหลืออีก {{ daysLeft }} วัน</template>
              <template v-else-if="currentEndDate">ใช้งานอยู่</template>
              <template v-else>ใช้งานได้ตลอด</template>
            </span>
            <span class="bp-current__note">
              <template v-if="currentEndDate">ใช้งานได้ถึง {{ formatDate(currentEndDate) }}</template>
              <template v-else>พื้นที่ 1 GB และฟีเจอร์พื้นฐาน</template>
            </span>
          </div>
        </div>

        <!--
          ผู้ใช้ที่ดูได้แต่ซื้อไม่ได้เป็นเรื่องปกติของหน้านี้ ไม่ใช่กรณีขอบ — ราคาจึงยัง
          อ่านได้เต็ม ไม่ทำการ์ดให้จาง เพราะคนที่เห็นคือคนที่จะไปบอกเจ้าของคลินิก
        -->
        <div v-if="!canPurchase" class="bp-locked">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="4.2" y="8.6" width="11.6" height="7.6" rx="2" stroke="currentColor" stroke-width="1.7" />
            <path d="M7 8.6V6.6a3 3 0 0 1 6 0v2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
          <div class="bp-locked__text">
            <span class="bp-locked__title">บัญชีของคุณดูแพ็กเกจได้ แต่ไม่มีสิทธิ์สั่งซื้อ</span>
            <span class="bp-locked__detail">ติดต่อเจ้าของคลินิกหรือผู้จัดการสาขาเพื่อให้ดำเนินการแทน</span>
          </div>
        </div>

        <div class="bp-cycle">
          <div class="bp-seg" role="group" aria-label="รอบการเรียกเก็บเงิน">
            <button type="button" class="bp-seg__btn" :class="{ 'is-on': !isYearly }" :aria-pressed="String(!isYearly)" @click="billingCycle = 'MONTHLY'">
              รายเดือน
            </button>
            <button type="button" class="bp-seg__btn" :class="{ 'is-on': isYearly }" :aria-pressed="String(isYearly)" @click="billingCycle = 'YEARLY'">
              รายปี<span class="bp-seg__save">ประหยัด 2 เดือน</span>
            </button>
          </div>
        </div>

        <div class="bp-grid" :class="`bp-grid--n${columnCount}`">
          <article
            v-for="plan in displayPlans"
            :key="plan.code"
            class="bp-card"
            :class="{
              'is-free': plan.isFree,
              'is-current': plan.isCurrent,
              'is-recommended': isRecommended(plan),
            }"
            :data-tier="plan.tier"
          >
            <span v-if="plan.isCurrent" class="bp-ribbon bp-ribbon--muted">ใช้อยู่</span>
            <span v-else-if="isRecommended(plan)" class="bp-ribbon">แนะนำ</span>

            <div class="bp-card__lead">
              <h2 class="bp-card__name">{{ plan.name }}</h2>
              <div class="bp-card__price">
                <span class="bp-card__amount">{{ formatMoney(monthlyFigure(plan, isYearly)) }}</span>
                <span class="bp-card__unit">บาท</span>
                <span class="bp-card__per">/ เดือน</span>
              </div>
              <p class="bp-card__note">{{ priceNote(plan) }}</p>
              <p v-if="savingOf(plan)" class="bp-save">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M8 13.5 3 8.5a3.2 3.2 0 0 1 4.5-4.5L8 4.5l.5-.5A3.2 3.2 0 0 1 13 8.5l-5 5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                </svg>
                {{ savingLabel(plan) }}
              </p>
            </div>

            <div class="bp-card__body">
              <!--
                ฟีเจอร์หลักไม่ได้อยู่ใน entitlements เพราะไม่เคยถูก gate ทุกสาขาได้ตั้งแต่ฟรี
                จึงเขียนไว้ตรงนี้ ไม่ได้ดึงจาก API และไม่โผล่ในตารางเทียบ เพราะสี่แถวที่ทุก
                คอลัมน์เป็น ✓ เหมือนกันหมดคือ noise ไม่ใช่ข้อมูลเลือกแพ็ก
                ถ้าวันไหนฟีเจอร์ไหนถูกขายเป็นสิทธิ์ ต้องย้ายไป entitlements ไม่ใช่แก้ลิสต์นี้

                กางครบทุกการ์ดรวมถึงฟรี ยอมให้ซ้ำสี่รอบ เพราะคนที่ยังไม่เคยใช้อ่านการ์ดเดียว
                แล้วตัดสินใจ ถ้าการ์ดไหนเห็นแค่ "1 GB, 5 เทมเพลต" จะไม่รู้เลยว่าระบบทำอะไรได้
              -->
              <p class="bp-card__core-heading">{{ coreHeading }}</p>
              <ul class="bp-card__core">
                <li v-for="core in coreFeatures" :key="core" class="bp-feat">
                  <span class="bp-feat__tick" aria-hidden="true">
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path d="M2.4 6.2 4.8 8.6 9.6 3.6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span class="bp-feat__text">{{ core }}</span>
                </li>
              </ul>

              <ul class="bp-card__quota">
                <li
                v-for="line in featureLinesFor(plan)"
                :key="line.key"
                class="bp-feat"
                :class="{ 'is-hero': line.highlight }"
              >
                <span class="bp-feat__tick" aria-hidden="true">
                  <!-- ดาวแทนเครื่องหมายถูกเฉพาะบรรทัดที่เป็นจุดขาย — ต่างที่รูปทรง ไม่ใช่แค่สี
                       คนตาบอดสีจึงยังแยกออก และคนที่กวาดตาเร็ว ๆ ก็เห็นว่าบรรทัดนี้ไม่เหมือนกัน -->
                  <svg v-if="line.highlight" width="11" height="11" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 .9l1.5 3.2 3.5.5-2.5 2.5.6 3.5L6 8.9 2.9 10.6l.6-3.5L1 4.6l3.5-.5L6 .9Z" />
                  </svg>
                  <svg v-else width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2.4 6.2 4.8 8.6 9.6 3.6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span class="bp-feat__text">
                  <!--
                    ช่องว่างเขียนเป็น {{ ' ' }} ไม่ใช่เว้นวรรคในเทมเพลต — ตัวคอมไพล์ condense
                    เก็บกวาด text node ที่มีแต่ช่องว่างทิ้ง ทำให้ได้ "พื้นที่เก็บไฟล์50 GB" ติดกัน
                  -->
                  {{ line.text }}<template v-if="line.strong">{{ " " }}<strong>{{ line.strong }}</strong></template
                  ><template v-if="line.suffix">{{ " " }}{{ line.suffix }}</template
                  ><sup v-if="line.footnote" class="bp-aster" aria-hidden="true">*</sup>
                </span>
                </li>
              </ul>
            </div>

            <button
              type="button"
              class="bp-cta"
              :class="ctaClass(plan)"
              :disabled="ctaDisabled(plan)"
              @click="choose(plan)"
            >
              <svg v-if="!plan.isCurrent && !plan.isFree && !canPurchase" width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="4.2" y="8.6" width="11.6" height="7.6" rx="2" stroke="currentColor" stroke-width="1.7" />
                <path d="M7 8.6V6.6a3 3 0 0 1 6 0v2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              </svg>
              {{ ctaLabel(plan) }}
            </button>
            <p v-if="plan.isFree && !plan.isCurrent" class="bp-card__fallback">
              กลับมาใช้อัตโนมัติเมื่อแพ็กปัจจุบันหมดอายุ
            </p>
          </article>
        </div>

        <!--
          การ์ดตอบว่า "แพ็กนี้ได้อะไร" ตารางตอบว่า "ต่างกันตรงไหน" ซึ่งการ์ดสี่ใบเรียงกัน
          ตอบไม่ได้ ตาคนไม่ได้ไล่เทียบเลขข้ามคอลัมน์จากบุลเล็ตสี่ชุดที่ยาวไม่เท่ากัน
        -->
        <section v-if="comparison.length > 1" class="bp-compare">
          <h2 class="bp-compare__title">เทียบทุกแพ็ก</h2>
          <div class="bp-compare__scroll">
            <table class="bp-table">
              <caption class="sr-only">ตารางเปรียบเทียบสิทธิ์ของแต่ละแพ็กเกจ</caption>
              <thead>
                <tr>
                  <th scope="col" class="bp-table__corner">ฟีเจอร์</th>
                  <th v-for="plan in displayPlans" :key="plan.code" scope="col" class="bp-table__head" :class="colClass(plan)" :data-tier="plan.tier">
                    <span class="bp-table__plan">{{ plan.name }}</span>
                    <span class="bp-table__price">{{ formatMoney(monthlyFigure(plan, isYearly)) }} บาท/เดือน</span>
                    <span v-if="plan.isCurrent" class="bp-table__chip">ใช้อยู่</span>
                    <span v-else-if="isRecommended(plan)" class="bp-table__chip bp-table__chip--rec">แนะนำ</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in comparison" :key="row.key" :class="{ 'is-same': !row.varies }">
                  <th scope="row" class="bp-table__label">
                    {{ row.label }}<sup v-if="row.footnote" class="bp-aster" aria-hidden="true">*</sup>
                  </th>
                  <td
                    v-for="(cell, i) in row.cells"
                    :key="i"
                    class="bp-table__cell"
                    :class="[`bp-table__cell--${cell.kind}`, colClass(displayPlans[i]), { 'is-hero': cell.hero }]"
                  >
                    <svg v-if="cell.text === 'มี'" width="16" height="16" viewBox="0 0 16 16" fill="none" :aria-label="cell.text" role="img">
                      <path d="M3.2 8.4 6.4 11.6 12.8 4.8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <template v-else>{{ cell.text }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!--
          ยอดที่ส่งไป 2C2P คือ priceMonthly/priceYearly ตรง ๆ (billing_service/createCheckout.js)
          ไม่มีที่ไหนบวก VAT เพิ่ม — ราคาที่โชว์จึงเท่ากับยอดที่ลูกค้าถูกตัดจริง
        -->
        <p class="bp-foot">ราคารวมภาษีมูลค่าเพิ่มแล้ว · เปลี่ยนแพ็กได้ทุกเมื่อ</p>
        <p class="bp-foot bp-foot--aster">
          * ผู้ช่วย AI ต้องเชื่อมผ่านบัญชี Claude หรือ ChatGPT ของคลินิกเอง · ข้อความแจ้งเตือน (push) ผ่าน LINE
          คิดค่าส่งตามจริง — ทั้งสองอย่างเป็นค่าใช้จ่ายของคลินิก ไม่รวมในแพ็ก
        </p>
      </template>

      <b-alert v-if="errorMessage" variant="danger" show class="mt-3 mb-0">{{ errorMessage }}</b-alert>
    </div>

    <b-modal ref="payMethodModal" modal-class="bp-modal" centered hide-header hide-footer no-close-on-backdrop>
      <div class="bp-modal__head">
        <div>
          <h2 class="bp-modal__title">ยืนยันการสั่งซื้อ</h2>
          <p class="bp-modal__sub">เลือกวิธีชำระเงิน แล้วระบบจะพาไปหน้าชำระเงินของผู้ให้บริการ</p>
        </div>
        <button type="button" class="bp-modal__x" aria-label="ปิด" @click="closeModal">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div v-if="selectedPlan" class="bp-modal__summary">
        <div class="bp-modal__summary-text">
          <span class="bp-modal__plan">{{ selectedPlan.name }} · {{ isYearly ? "รายปี" : "รายเดือน" }}</span>
        </div>
        <div class="bp-modal__summary-price">
          <span class="bp-modal__amount">{{ formatMoney(priceOf(selectedPlan)) }}</span>
          <span class="bp-modal__vat">บาท (รวมภาษีมูลค่าเพิ่มแล้ว)</span>
        </div>
      </div>

      <fieldset class="bp-modal__methods">
        <legend>วิธีชำระเงิน</legend>
        <label class="bp-method" :class="{ 'is-on': paymentMethod === 'CARD' }" for="bp-pm-card">
          <input id="bp-pm-card" v-model="paymentMethod" type="radio" name="bp-paymethod" value="CARD" />
          <span class="bp-method__text">
            <span class="bp-method__name">บัตรเครดิต/เดบิต</span>
            <span class="bp-method__detail">ต่ออายุอัตโนมัติทุกรอบ ยกเลิกได้ตลอดเวลา</span>
          </span>
          <span class="bp-method__tag">แนะนำ</span>
        </label>
        <label class="bp-method" :class="{ 'is-on': paymentMethod === 'PROMPTPAY' }" for="bp-pm-pp">
          <input id="bp-pm-pp" v-model="paymentMethod" type="radio" name="bp-paymethod" value="PROMPTPAY" />
          <span class="bp-method__text">
            <span class="bp-method__name">พร้อมเพย์</span>
            <span class="bp-method__detail">จ่ายครั้งเดียว ต่ออายุอัตโนมัติไม่ได้ ต้องกลับมาจ่ายเองก่อนหมดอายุ</span>
          </span>
        </label>
      </fieldset>

      <p class="bp-modal__safe">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 1.6 3 3.7v3.7c0 3.1 2.1 5.9 5 6.9 2.9-1 5-3.8 5-6.9V3.7L8 1.6Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
          <path d="M6 7.9 7.4 9.3 10.2 6.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        ชำระเงินผ่านระบบของผู้ให้บริการ — DentCloud ไม่เก็บเลขบัตรของคุณ
      </p>

      <div class="bp-modal__foot">
        <button type="button" class="bp-btn bp-btn--ghost" :disabled="isSubmitting" @click="closeModal">ยกเลิก</button>
        <button type="button" class="bp-btn bp-btn--solid bp-btn--grow" :disabled="isSubmitting" @click="startCheckout">
          <b-spinner v-if="isSubmitting" small class="mr-2" />
          {{ isSubmitting ? "กำลังเชื่อมต่อ…" : "ไปหน้าชำระเงิน" }}
          <svg v-if="!isSubmitting" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";
import {
  comparisonRows,
  featureLines,
  formatMoney,
  monthlyFigure,
  recommendedPlanCode,
  yearlySaving,
} from "@/utils/billingPlanPresenter";

// ใกล้หมดอายุเท่าไรถึงเตือน — สองสัปดาห์พอให้ผู้จัดการสาขาขออนุมัติทัน
const EXPIRY_WARNING_DAYS = 14;

// กริดกว้างสุด 4 คอลัมน์ จำนวนแพ็กมาจาก BE และเพิ่มได้โดยไม่ต้อง deploy หน้านี้
const MAX_COLUMNS = 4;

// ฟีเจอร์พื้นฐานที่ทุกสาขาได้ ไม่ว่าจะจ่ายเงินหรือไม่ — ไม่ได้อยู่ใน entitlements
// เพราะไม่เคยถูก gate ถ้าวันไหนถูกขายเป็นสิทธิ์ ต้องย้ายไป entitlements ไม่ใช่แก้ที่นี่
//
// ทุกคำอ้างถึงโมดูลที่มีจริงในระบบ ไม่ได้เขียนเกิน: ลิสต์นัดหมาย/จัดการคิวการรักษา,
// ลิสต์การรักษา, จัดการใบเสร็จ + แก้ไข DF, รายได้สาขา + แดชบอร์ด
// หัวกลุ่มเป็นตัวขาย ไม่ใช่ตัวรายการ — เล่าเป็นเส้นทางของคนไข้ (นัดหมาย → รักษา → จ่ายเงิน)
// ไม่ใช่รายชื่อโมดูล คนอ่านเห็นภาพว่าใช้ระบบเดียวจบทั้งวัน แล้วค่อยไล่ว่ามีอะไรบ้าง
// สองบรรทัดพอ: บรรทัดแรกฝั่งดูแลคนไข้ บรรทัดสองฝั่งเงินและตัวเลข
const CORE_HEADING = "ดูแลคนไข้ได้ตั้งแต่นัดหมายจนชำระเงิน";
const CORE_FEATURES = [
  "นัดหมาย คิว และเวชระเบียน",
  "ใบเสร็จ DF รายงาน และแดชบอร์ด",
];

export default {
  name: "BillingPlans",
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      billingCycle: "YEARLY",
      paymentMethod: "CARD",
      plans: [],
      freePlan: null,
      currentPlanCode: "FREE",
      currentEndDate: null,
      selectedPlan: null,
      canPurchase: true,
      loadError: false,
      errorMessage: "",
      orderNumber: null,
      returnedOrder: null,
      isPolling: false,
      pollTimer: null,
      coreHeading: CORE_HEADING,
      coreFeatures: CORE_FEATURES,
      pollsLeft: 0,
      // รอบการถามสถานะปัจจุบัน — คำตอบที่ค้างอยู่ของรอบเก่าต้องไม่ตั้ง timer ต่อ
      pollRun: 0,
    };
  },
  computed: {
    isYearly() {
      return this.billingCycle === "YEARLY";
    },
    /**
     * แพ็กที่เอาขึ้นจอ = ฟรี (ถ้ามี) แล้วตามด้วยแพ็กที่ซื้อได้
     *
     * BE ส่งฟรีมาคนละฟิลด์กับ plans โดยตั้งใจ เพราะฟรีซื้อไม่ได้ createCheckout ปฏิเสธ
     * มันตรง ๆ ที่นี่จึงติดธง isFree ไว้ตอนรวม แทนที่จะให้เทมเพลตไปเทียบรหัส "FREE" เอง
     * ซึ่งเท่ากับ hardcode รหัสแพ็กไว้ในหน้าจอ
     */
    displayPlans() {
      const list = this.freePlan ? [{ ...this.freePlan, isFree: true }, ...this.plans] : this.plans;
      // ระดับสี ไล่จากจางไปเข้มตามลำดับที่ BE ส่งมา (เรียงตามราคาอยู่แล้ว) — สีเดียว
      // คนละความเข้ม ไม่ใช่คนละสี เพื่อให้ยังเป็นธีมเดียวกับทั้งแอป
      // ตัน 3 เพราะเกินสี่ขั้นแล้วตาแยกความเข้มไม่ออก กลายเป็นฟ้าเข้มเหมือนกันหมด
      return list.map((plan, i) => ({ ...plan, tier: Math.min(i, 3) }));
    },
    columnCount() {
      return Math.min(Math.max(this.displayPlans.length, 1), MAX_COLUMNS);
    },
    recommendedCode() {
      return recommendedPlanCode(this.plans);
    },
    comparison() {
      return comparisonRows(this.displayPlans);
    },
    currentPlanName() {
      const found = this.displayPlans.find((p) => p.code === this.currentPlanCode);
      return found ? found.name : "ฟรี";
    },
    daysLeft() {
      if (!this.currentEndDate) return null;
      const left = moment(this.currentEndDate).endOf("day").diff(moment(), "days");
      return left >= 0 && left <= EXPIRY_WARNING_DAYS ? left : null;
    },
    expiresSoon() {
      return this.daysLeft !== null;
    },
    showResult() {
      return this.isPolling || !!this.returnedOrder;
    },
    /**
     * โทนของแถบผลการชำระเงิน
     *
     * 'pending' (ถามจนครบรอบแล้วยังไม่รู้ผล) **ไม่ใช่** การจ่ายไม่สำเร็จ และต้องไม่
     * ใช้โทนเดียวกับ 'failed' — ผู้ใช้ที่อ่านว่าจ่ายไม่ผ่านทั้งที่เงินตัดไปแล้วจะจ่ายซ้ำ
     */
    resultTone() {
      if (this.isPolling) return "waiting";
      if (!this.returnedOrder) return "waiting";
      if (this.returnedOrder.status === "COMPLETED") return "success";
      if (this.returnedOrder.status === "FAILED") return "failed";
      return "pending";
    },
    purchasedSubscription() {
      return (this.returnedOrder && this.returnedOrder.subscription) || null;
    },
    purchasedPlanName() {
      const sub = this.purchasedSubscription;
      if (!sub) return "";
      const found = this.plans.find((p) => p.code === sub.planCode);
      return found ? found.name : sub.planCode;
    },
    purchasedEndDate() {
      return this.purchasedSubscription ? this.purchasedSubscription.endDate : null;
    },
  },
  created() {
    this.fetchPlans();
    this.resumeReturnedOrder();
  },
  beforeDestroy() {
    // ไม่ปล่อย timer ค้างหลังออกจากหน้า — จะยิง API ต่อไปเรื่อย ๆ โดยไม่มีใครดูผล
    this.stopPolling();
  },
  methods: {
    ...mapActions("moduleBilling", ["createCheckout", "fetchOrderStatus"]),

    formatMoney,
    monthlyFigure,
    featureLinesFor: featureLines,

    savingOf(plan) {
      return yearlySaving(plan);
    },
    /**
     * ป้ายส่วนลดขึ้นทั้งสองรอบ แต่คนละหน้าที่ — รอบรายปีเป็นคำยืนยันว่าประหยัดอยู่
     * รอบรายเดือนเป็นคำชวนว่าถ้าย้ายไปรายปีจะประหยัดเท่าไร ตัวเลขเดียวกันทั้งคู่
     */
    savingLabel(plan) {
      const saving = yearlySaving(plan);
      if (!saving) return "";
      const amount = `${formatMoney(saving)} บาท/ปี`;
      return this.isYearly ? `ประหยัด ${amount}` : `จ่ายรายปีประหยัด ${amount}`;
    },

    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    priceOf(plan) {
      return this.isYearly ? plan.priceYearly : plan.priceMonthly;
    },
    /**
     * บรรทัดรองใต้ราคา — บอกยอดที่ "ถูกเรียกเก็บจริง" ให้ชัด
     *
     * ตัวเลขใหญ่เป็นค่าเฉลี่ยต่อเดือนเสมอ ซึ่งตอนเลือกรายปีไม่ใช่ยอดที่ตัดบัตร
     * บรรทัดนี้จึงต้องมี ห้ามตัดทิ้ง ไม่งั้นกลายเป็นโฆษณาราคาที่ไม่มีใครจ่ายจริง
     */
    priceNote(plan) {
      if (!this.priceOf(plan)) return "ไม่มีค่าใช้จ่าย";
      if (!this.isYearly) return "เรียกเก็บทุกเดือน";
      return `เรียกเก็บปีละ ${formatMoney(plan.priceYearly)} บาท`;
    },
    isRecommended(plan) {
      return !plan.isCurrent && plan.code === this.recommendedCode;
    },
    ctaLabel(plan) {
      if (plan.isCurrent) return "ใช้แพ็กนี้อยู่";
      // ฟรีไม่ใช่ของที่กดซื้อหรือกดลดขั้นได้ มันคือที่ที่สาขาตกลงมาเองเมื่อแพ็กหมดอายุ
      if (plan.isFree) return "แพ็กพื้นฐาน";
      if (!this.canPurchase) return "ต้องมีสิทธิ์สั่งซื้อ";
      return "เลือกแพ็กนี้";
    },
    ctaDisabled(plan) {
      return plan.isCurrent || plan.isFree || !this.canPurchase || this.isSubmitting;
    },
    colClass(plan) {
      if (!plan) return null;
      if (plan.isCurrent) return "is-current-col";
      return this.isRecommended(plan) ? "is-rec-col" : null;
    },
    ctaClass(plan) {
      if (plan.isCurrent || plan.isFree || !this.canPurchase) return "bp-cta--off";
      return this.isRecommended(plan) ? "bp-cta--solid" : "bp-cta--outline";
    },

    async fetchPlans() {
      this.isLoading = true;
      this.loadError = false;
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        const data = await this.$store.dispatch("moduleBilling/fetchPlans", { clinicUrl, branchUrl });
        this.plans = data.plans || [];
        this.freePlan = data.freePlan || null;
        this.currentPlanCode = data.currentPlanCode || "FREE";
        this.currentEndDate = data.currentEndDate || null;
      } catch (err) {
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ผู้ใช้กลับมาจากหน้าจ่ายเงิน — ถามสถานะจนกว่าจะรู้ผล
     *
     * **การกลับมาถึงหน้านี้ไม่ได้แปลว่าจ่ายสำเร็จ** ผู้ใช้กลับมาก่อน webhook เข้าเป็นเรื่องปกติ
     * และ URL ปลอมได้ ทุกอย่างที่แสดงมาจากคำตอบของ server ไม่ใช่จาก query string
     *
     * เลข order เอาจาก query ก่อน (2C2P พากลับมาพร้อมพารามิเตอร์) แล้วค่อยถอยไปดู
     * sessionStorage เผื่อผู้ให้บริการตัด query ทิ้ง
     */
    resumeReturnedOrder() {
      let orderNumber = this.$route.query.order;
      if (!orderNumber) {
        try {
          orderNumber = sessionStorage.getItem("billingPendingOrder");
        } catch (e) {
          orderNumber = null;
        }
      }
      if (!orderNumber) return;

      this.orderNumber = orderNumber;
      this.startPolling();
    },

    /**
     * จำกัดจำนวนครั้ง ไม่วนไม่สิ้นสุด — ถ้าเกินแล้วยังไม่รู้ผล บอกให้ผู้ใช้กลับมาดูทีหลัง
     * ดีกว่าปล่อยให้หน้าค้างหมุนตลอดกาลหรือถล่ม API
     */
    startPolling() {
      this.stopPolling();
      this.pollsLeft = 10;
      this.isPolling = true;
      this.pollOrder(this.orderNumber, this.pollRun);
    },
    /**
     * เลิกถามสถานะ
     *
     * เดิน pollRun ไปหนึ่งขั้นด้วย ไม่ใช่แค่ล้าง timer — คำขอที่ยิงไปแล้วและยังไม่กลับมา
     * จะกลับมาหลังจากนี้เสมอ ถ้าไม่ทำให้มันรู้ว่าตัวเองเป็นของรอบเก่า มันจะตั้ง timer
     * รอบใหม่ต่อ กลายเป็นถามซ้อนกันสองสาย (กด "ตรวจสอบอีกครั้ง" ตอนมีคำขอค้างอยู่)
     * หรือยิง API ต่อหลังออกจากหน้าไปแล้ว
     */
    stopPolling() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.isPolling = false;
      this.pollRun += 1;
    },

    /** ปุ่ม "ตรวจสอบอีกครั้ง" ของสถานะที่ยังไม่รู้ผล — ถามใหม่ ไม่ล้างเลขคำสั่งซื้อทิ้ง */
    recheckOrder() {
      if (!this.orderNumber) return;
      this.startPolling();
    },

    /** ปิดแถบผลที่รู้ผลแล้ว สถานะที่ยังค้างไม่มีปุ่มนี้ เพื่อไม่ให้ผู้ใช้ปัดทิ้งโดยไม่รู้ผล */
    dismissResult() {
      this.returnedOrder = null;
      this.orderNumber = null;
    },

    async pollOrder(orderNumber, run) {
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        const data = await this.fetchOrderStatus({ clinicUrl, branchUrl, orderNumber });
        if (run !== this.pollRun) return;
        this.returnedOrder = data;

        if (data.settled) {
          this.stopPolling();
          try {
            sessionStorage.removeItem("billingPendingOrder");
          } catch (e) {
            // ไม่เป็นไร ค่าที่ค้างอยู่จะถูกทับตอนซื้อครั้งถัดไป
          }
          // สิทธิ์เพิ่งเปลี่ยน โหลดแพ็กใหม่เพื่อให้หน้าจอตรงกับความจริง
          if (data.status === "COMPLETED") this.fetchPlans();
          return;
        }
      } catch (err) {
        if (run !== this.pollRun) return;
        // 404 = เลขที่ค้างอยู่ไม่ใช่ของสาขานี้ (เปลี่ยนสาขาแล้ว) เลิกถามและล้างทิ้ง
        if (err?.response?.status === 404) {
          this.stopPolling();
          this.returnedOrder = null;
          this.orderNumber = null;
          try {
            sessionStorage.removeItem("billingPendingOrder");
          } catch (e) {
            // เงียบไว้ ไม่มีอะไรให้ทำต่อ
          }
          return;
        }
      }

      this.pollsLeft -= 1;
      if (this.pollsLeft <= 0) {
        this.stopPolling();
        return;
      }
      this.pollTimer = setTimeout(() => this.pollOrder(orderNumber, run), 3000);
    },

    choose(plan) {
      // กันซ้ำที่ตรรกะ ไม่ใช่แค่ที่ปุ่ม — BE ตอบ 400 ให้ FREE อยู่แล้ว ไม่ต้องไปให้ถึงตรงนั้น
      if (plan.isFree) return;
      this.selectedPlan = plan;
      this.errorMessage = "";
      this.$refs.payMethodModal.show();
    },
    closeModal() {
      this.$refs.payMethodModal.hide();
    },

    async startCheckout() {
      if (!this.selectedPlan || this.isSubmitting) return;
      this.isSubmitting = true;
      this.errorMessage = "";
      try {
        const { clinicUrl, branchUrl } = this.$route.params;
        const result = await this.createCheckout({
          clinicUrl,
          branchUrl,
          planCode: this.selectedPlan.code,
          billingCycle: this.billingCycle,
          paymentMethod: this.paymentMethod,
        });

        // จำเลขคำสั่งซื้อไว้ก่อนออกจากหน้า — ผู้ใช้กลับมาแล้วต้องถามสถานะต่อได้
        // ห้ามถือว่าซื้อสำเร็จเพราะเดินทางกลับมาถึง สิทธิ์เปิดโดย webhook ฝั่ง server เท่านั้น
        try {
          sessionStorage.setItem("billingPendingOrder", result.orderNumber);
        } catch (e) {
          // โหมดส่วนตัวหรือปิด storage — ไม่ใช่เหตุให้หยุดการซื้อ แค่กลับมาแล้วต้องรีเฟรชเอง
        }

        window.location.href = result.webPaymentUrl;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          this.canPurchase = false;
          this.errorMessage = "บัญชีของคุณไม่มีสิทธิ์สั่งซื้อแพ็กเกจให้สาขานี้";
        } else {
          this.errorMessage =
            err?.response?.data?.message || "ไม่สามารถเริ่มการชำระเงินได้ กรุณาลองใหม่อีกครั้ง";
        }
        this.$refs.payMethodModal.hide();
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$bp-ground: #f6f7f9;
$bp-card: #ffffff;
$bp-line: #e3e6eb;
$bp-rule: #edf0f4;
$bp-ink: #101828;
$bp-body: #475467;
$bp-muted: #8c95a6;
$bp-faint: #98a2b3;
// ฟ้าเดียวกับทั้งแอป: ไม่มีที่ไหน override $primary ของ Bootstrap เลย ค่าจึงเป็น #007bff
// (btn-primary ทุกปุ่มในระบบใช้ตัวนี้) — accent-ink คือเฉดเข้มสำหรับ "ตัวอักษรบนพื้นขาว"
// เพราะ #007bff บนขาวได้คอนทราสต์ 3.98:1 ตกเกณฑ์ AA ของข้อความขนาดปกติ
$bp-accent: #007bff;
$bp-accent-dark: #0062cc;
$bp-accent-ink: #0056b3;
$bp-accent-tint: #e8f2ff;

.view-billing-plans {
  padding: 0;
  background: $bp-ground;
}

.bp {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 40px 40px;
}

.bp-head {
  &__title {
    margin: 0 0 6px;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: -0.2px;
    color: $bp-ink;
  }
  &__sub {
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: #667085;
  }
}

/* ---------- แถบผลการชำระเงิน ---------- */
.bp-result {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px 24px;
  border-radius: 16px;
  border: 1px solid;

  &__icon {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__text {
    flex-grow: 1;
    min-width: 0;
  }
  &__title {
    margin: 0 0 5px;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
  }
  &__detail {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
  }
  &__meta {
    margin: 5px 0 0;
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    opacity: 0.75;
  }
  &__side {
    flex-shrink: 0;
  }

  // กำลังตรวจสอบ และ ยังไม่ได้รับการยืนยัน อยู่ตระกูล "รอ" ด้วยกัน
  &--waiting {
    background: $bp-card;
    border-color: #dde3ea;
    .bp-result__icon {
      background: #eff3f8;
      color: $bp-body;
    }
    .bp-result__title {
      color: $bp-ink;
    }
    .bp-result__detail {
      color: #667085;
    }
  }
  &--pending {
    background: #fffbf2;
    border-color: #f0ddb8;
    .bp-result__icon {
      background: #fbeed1;
      color: #90570a;
    }
    .bp-result__title {
      color: #6b4208;
    }
    .bp-result__detail {
      color: #8a6021;
    }
  }
  &--success {
    background: #f4fbf7;
    border-color: #c6e9d6;
    .bp-result__icon {
      background: #d6f2e2;
      color: #07683f;
    }
    .bp-result__title {
      color: #05301e;
    }
    .bp-result__detail {
      color: #3d6552;
    }
  }
  &--failed {
    background: #fef6f5;
    border-color: #f3c9c4;
    .bp-result__icon {
      background: #fbddd9;
      color: #a03127;
    }
    .bp-result__title {
      color: #6e211a;
    }
    .bp-result__detail {
      color: #8a4139;
    }
  }
}

.bp-progress {
  width: 180px;
  height: 5px;
  border-radius: 999px;
  background: #e6eaf0;
  overflow: hidden;

  span {
    display: block;
    width: 34%;
    height: 100%;
    border-radius: 999px;
    background: $bp-faint;
    animation: bp-slide 1.4s ease-in-out infinite;
  }
}

@keyframes bp-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(295%);
  }
}

/* ---------- แพ็กปัจจุบัน ---------- */
.bp-current {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px 24px;
  background: $bp-card;
  border: 1px solid $bp-line;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);

  &__icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: $bp-accent-tint;
    color: $bp-accent;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__main {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  &__label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: $bp-muted;
  }
  &__name {
    font-size: 19px;
    font-weight: 600;
    color: $bp-ink;
  }
  &__side {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }
  &__note {
    font-size: 13px;
    color: $bp-muted;
  }
}

.bp-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;

  &--calm {
    background: #f2f4f7;
    color: $bp-body;
  }
  &--warn {
    background: #fff4e0;
    color: #90570a;
  }
}

/* ---------- ไม่มีสิทธิ์สั่งซื้อ ---------- */
.bp-locked {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 20px;
  background: #f4f7fc;
  border: 1px solid #d3dff0;
  border-radius: 14px;
  color: #1b4c8f;

  svg {
    flex-shrink: 0;
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  &__title {
    font-size: 14.5px;
    font-weight: 600;
    color: #123a70;
  }
  &__detail {
    font-size: 13px;
    line-height: 1.5;
    color: #40618c;
  }
}

/* ---------- สลับรอบเรียกเก็บ ---------- */
.bp-cycle {
  display: flex;
  justify-content: center;
}

.bp-seg {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: #eceff3;

  &__btn {
    appearance: none;
    border: 0;
    background: transparent;
    color: #667085;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 9px 20px;
    min-height: 44px;
    border-radius: 999px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.15s ease, color 0.15s ease;

    &.is-on {
      background: $bp-card;
      color: $bp-accent-ink;
      box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12);
    }
  }
  &__save {
    padding: 2px 8px;
    border-radius: 999px;
    background: #d6e9ff;
    color: $bp-accent-ink;
    font-size: 11px;
    font-weight: 700;
  }
}

/* ---------- ไล่ความเข้มตามลำดับแพ็ก ---------- */
//
// สีเดียวคนละความเข้ม ไม่ใช่คนละสี — แพ็กยิ่งสูงยิ่งเข้ม สายตาจึงจับลำดับได้ก่อนอ่านราคา
// ค่าทั้งหมดอยู่ในตระกูลฟ้าของแอป (#007bff) ระดับ 0 เป็นกลางเพราะฟรีไม่ใช่สินค้า
//
// --tier-ink ใช้กับตัวอักษรบนพื้นขาวเท่านั้น ทุกค่าผ่าน AA 4.5:1
// --tier-mark ใช้กับพื้นไอคอนและแถบบนหัวตาราง ซึ่งไม่ใช่ข้อความ
.bp-card,
.bp-table__head {
  --tier-ink: #{$bp-accent-ink};
  --tier-mark: #{$bp-accent};
  --tier-tint: #{$bp-accent-tint};
}

.bp-card[data-tier="0"],
.bp-table__head[data-tier="0"] {
  --tier-ink: #566172;
  --tier-mark: #98a2b3;
  --tier-tint: #f2f4f7;
}
.bp-card[data-tier="1"],
.bp-table__head[data-tier="1"] {
  --tier-ink: #0065c8;
  --tier-mark: #4da3ff;
  --tier-tint: #edf5ff;
}
.bp-card[data-tier="2"],
.bp-table__head[data-tier="2"] {
  --tier-ink: #0056b3;
  --tier-mark: #007bff;
  --tier-tint: #e2efff;
}
.bp-card[data-tier="3"],
.bp-table__head[data-tier="3"] {
  --tier-ink: #00408a;
  --tier-mark: #0047ab;
  --tier-tint: #dbe8fb;
}

// แถบบางบนหัวการ์ด — ตัวบอกลำดับที่อ่านได้ก่อนตัวหนังสือ
.bp-card::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 18px;
  right: 18px;
  height: 3px;
  border-radius: 0 0 3px 3px;
  background: var(--tier-mark);
}

.bp-table__head::after {
  content: "";
  display: block;
  height: 3px;
  margin: 10px -16px -14px;
  background: var(--tier-mark);
}

/* ---------- กริดแพ็ก ---------- */
.bp-grid {
  display: grid;
  gap: 20px;
  align-items: stretch;

  &--n1 {
    grid-template-columns: minmax(0, 1fr);
  }
  &--n2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  &--n3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  &--n4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.bp-card {
  position: relative;
  display: grid;
  grid-template-areas: "lead" "body" "cta" "note";
  grid-template-rows: auto 1fr auto auto;
  box-sizing: border-box;
  padding: 26px 24px;
  background: $bp-card;
  border: 1px solid $bp-line;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);

  &.is-current {
    border-color: #c9d0d8;
  }
  &.is-recommended {
    border: 1.5px solid $bp-accent;
    box-shadow: 0 12px 30px rgba(0, 123, 255, 0.16);
  }

  &__lead {
    grid-area: lead;
  }
  &__name {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: var(--tier-ink, #{$bp-ink});
  }
  &__price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-top: 18px;
  }
  &__amount {
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
    color: $bp-ink;
    font-variant-numeric: tabular-nums;
  }
  &__unit {
    font-size: 15px;
    font-weight: 500;
    color: #667085;
  }
  &__per {
    font-size: 15px;
    color: $bp-muted;
  }
  &__note {
    margin: 7px 0 0;
    font-size: 13px;
    color: $bp-muted;
  }
  &__body {
    grid-area: body;
    margin: 22px 0 0;
    padding: 18px 0 0;
    border-top: 1px solid $bp-rule;
  }
  // เส้นคั่นเป็น border-bottom ของกลุ่ม ไม่ใช่ border-top ของแถวแรกกลุ่มถัดไป
  // เพราะแถวแรกนั้นอาจเป็นแถว hero ซึ่งมีพื้นหลังฟ้าอ่อนกับ margin ติดลบ แล้วทับเส้น
  // จนกลืนหาย (เจอจริงที่การ์ดพรีเมียม ซึ่งแถวแรกคือ "พื้นที่เก็บไฟล์ ไม่จำกัด")
  &__core {
    margin: 0 0 14px;
    padding: 0 0 14px;
    border-bottom: 1px solid $bp-rule;
    list-style: none;
  }
  &__quota {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  // หัวกลุ่มฟีเจอร์หลัก ทำหน้าที่เป็นคำโปรยสั้น ๆ แทนที่จะเขียนแต่ละข้อให้ยาว
  &__core-heading {
    margin: 0 0 10px;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: var(--tier-ink, #{$bp-accent-ink});
  }
}

// การ์ดฟรีไม่ใช่สินค้า ลดน้ำหนักลงหนึ่งขั้นให้ไม่แย่งสายตาจากแพ็กที่ขายจริง
// แต่ยังอ่านได้เต็ม เพราะมันคือเส้นฐานที่ผู้ใช้ใช้เทียบ
.bp-card.is-free {
  background: #fbfcfd;

  .bp-card__amount {
    color: $bp-body;
  }
  .bp-feat__tick {
    background: #f2f4f7;
    color: #667085;
  }
}

.bp-card__fallback {
  grid-area: note;
  margin: 8px 0 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.45;
  color: $bp-faint;
}

.bp-ribbon {
  position: absolute;
  top: -12px;
  left: 24px;
  z-index: 1;
  padding: 4px 12px;
  border-radius: 999px;
  background: $bp-accent;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;

  &--muted {
    background: #e7eaee;
    color: $bp-body;
  }
}

.bp-feat {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 11px;

  &:last-child {
    margin-bottom: 0;
  }

  &__tick {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 3px;
    border-radius: 999px;
    background: var(--tier-tint, #{$bp-accent-tint});
    color: var(--tier-mark, #{$bp-accent});
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__text {
    font-size: 14px;
    line-height: 1.5;
    color: $bp-body;

    strong {
      font-weight: 600;
      color: $bp-ink;
    }
  }

  /*
    บรรทัดที่เป็นจุดขาย (ไม่จำกัด / ไม่มีโฆษณา) — เน้นด้วยสามอย่างพร้อมกัน ไม่ใช่สีอย่างเดียว:
    รูปทรงไอคอนต่าง (ดาวแทนถูก), พื้นหลังอ่อน, และคำที่เน้นใหญ่ขึ้น
    คนตาบอดสีจึงยังแยกออก และคนที่กวาดตาเร็ว ๆ เห็นความต่างก่อนอ่านข้อความ
  */
  &.is-hero {
    margin-left: -8px;
    margin-right: -8px;
    padding: 6px 8px;
    border-radius: 8px;
    background: $bp-accent-tint;

    .bp-feat__tick {
      background: $bp-accent;
      color: #fff;
    }

    /*
      บรรทัดที่มีตัวเลข (ไม่จำกัด) เน้นที่คำนั้น ส่วนบรรทัดที่ไม่มีตัวเลข (ไม่มีโฆษณา)
      เน้นทั้งบรรทัด — การฝืนแยกคำออกมาเป็น strong ทำให้ประโยคภาษาไทยขาดเป็นท่อน ๆ
    */
    .bp-feat__text {
      color: $bp-accent-dark;
      font-weight: 600;

      strong {
        font-size: 15px;
        font-weight: 700;
        color: $bp-accent-dark;
      }
    }
  }
}

/* การ์ดของแพ็กที่ใช้อยู่ถูกลดน้ำหนักทั้งใบ — บรรทัดจุดขายก็ต้องจางตาม ไม่งั้นมันจะเด่น
   กว่าการ์ดที่ยังขายอยู่ ทั้งที่ไม่มีอะไรให้กดแล้ว */
.bp-card.is-current .bp-feat__tick {
  background: #f2f4f7;
  color: #667085;
}

.bp-card.is-current .bp-feat.is-hero {
  background: #f7f8fa;

  .bp-feat__tick {
    background: #e4e7ec;
    color: #667085;
  }

  .bp-feat__text strong {
    color: $bp-ink;
  }
}

.bp-cta {
  grid-area: cta;
  margin-top: 20px;
  width: 100%;
  min-height: 46px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &--solid {
    border: 0;
    background: $bp-accent;
    color: #ffffff;

    &:hover {
      background: $bp-accent-dark;
    }
  }
  &--outline {
    border: 1px solid var(--tier-mark, #{$bp-accent});
    background: $bp-card;
    color: var(--tier-ink, #{$bp-accent-ink});

    &:hover {
      background: var(--tier-tint, #{$bp-accent-tint});
    }
  }
  &--off {
    border: 1px solid $bp-line;
    background: #f8f9fb;
    color: $bp-faint;
    cursor: default;
  }
}

.bp-foot {
  margin: 0;
  text-align: center;
  font-size: 13px;
  line-height: 1.5;
  color: $bp-muted;

  // เงื่อนไขปลีกย่อย ไม่ใช่สิ่งที่ใช้เลือกแพ็ก — จางกว่าและเล็กกว่าบรรทัดบน
  &--aster {
    margin-top: -16px;
    font-size: 12px;
    color: $bp-faint;
  }
}

.bp-aster {
  margin-left: 1px;
  font-size: 0.72em;
  line-height: 0;
  color: $bp-faint;
}

.bp-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 10px 0 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e6f6ec;
  color: #0f6b3f;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  svg {
    flex-shrink: 0;
  }
}

/* ---------- ตารางเทียบทุกแพ็ก ---------- */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.bp-compare {
  &__title {
    margin: 0 0 14px;
    font-size: 17px;
    font-weight: 600;
    color: $bp-ink;
  }
  &__scroll {
    overflow-x: auto;
    background: $bp-card;
    border: 1px solid $bp-line;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }
}

.bp-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    text-align: center;
    padding: 14px 16px;
    border-bottom: 1px solid $bp-rule;
  }
  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: 0;
  }

  // คอลัมน์ชื่อฟีเจอร์ตรึงไว้ซ้ายมือ เลื่อนตารางแนวนอนแล้วยังรู้ว่ากำลังอ่านแถวอะไร
  &__corner,
  &__label {
    position: sticky;
    left: 0;
    z-index: 1;
    text-align: left;
    background: $bp-card;
  }
  &__corner {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: $bp-muted;
  }
  &__label {
    font-size: 14px;
    font-weight: 500;
    color: $bp-body;
    min-width: 240px;
  }

  &__head {
    vertical-align: bottom;
  }
  &__plan {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: $bp-ink;
  }
  &__price {
    display: block;
    margin-top: 2px;
    font-size: 12.5px;
    font-weight: 400;
    color: $bp-muted;
    font-variant-numeric: tabular-nums;
  }
  &__chip {
    display: inline-block;
    margin-top: 7px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #e7eaee;
    color: $bp-body;
    font-size: 10.5px;
    font-weight: 700;

    &--rec {
      background: $bp-accent;
      color: #ffffff;
    }
  }

  // น้ำหนักของช่องคือสิ่งที่ทำให้ส่วนต่างเด่น: ตัวเลขเข้ม ได้แบบไม่จำกัดเป็นสีหลัก
  // ส่วนที่ไม่ได้จางจนอ่านออกว่า "ไม่มี" โดยไม่ต้องอ่านคำ
  &__cell {
    font-variant-numeric: tabular-nums;

    &--value {
      font-weight: 600;
      color: $bp-ink;
    }
    &--yes {
      font-weight: 600;
      color: $bp-accent-ink;
    }
    &--no {
      color: #c3c9d4;
    }
  }

  // แถวที่ทุกแพ็กได้เท่ากันไม่ช่วยตัดสินใจ หรี่ลงให้แถวที่ต่างกันเด่นแทน
  tbody tr.is-same {
    .bp-table__label {
      color: $bp-faint;
      font-weight: 400;
    }
    .bp-table__cell--value,
    .bp-table__cell--yes {
      font-weight: 400;
      color: $bp-muted;
    }
  }

  // ช่องที่ได้ของที่แพ็กอื่นในแถวไม่ได้ — ทรีตเมนต์เดียวกับดาวบนการ์ด ต่างที่พื้นหลัง
  // ไม่ใช่แค่สีตัวอักษร คนตาบอดสีจึงยังแยกออกจากช่องตัวเลขธรรมดา
  &__cell.is-hero {
    background: $bp-accent-tint;
    color: $bp-accent-ink;
    font-weight: 700;
    box-shadow: inset 0 0 0 1px rgba(0, 123, 255, 0.18);
  }

  .is-current-col {
    background: #f7f8fa;
  }
  .is-rec-col {
    background: #f4f9ff;
  }
}

/* ---------- โหลด / ผิดพลาด ---------- */
.bp-skeleton {
  box-sizing: border-box;
  min-height: 268px;
  padding: 24px;
  background: $bp-card;
  border: 1px solid #e9ecf1;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 13px;

  &__bar {
    display: block;
    height: 12px;
    border-radius: 6px;
    background: #f2f4f7;
  }
  &__rule {
    display: block;
    height: 1px;
    margin: 6px 0;
    background: $bp-rule;
  }
  &__cta {
    display: block;
    margin-top: auto;
    height: 44px;
    border-radius: 10px;
    background: #edf0f4;
  }
}

.bp-empty {
  padding: 40px 24px;
  background: $bp-card;
  border: 1px solid #dde3ea;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 13px;
    background: #fef3f2;
    color: #a03127;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $bp-ink;
  }
  &__detail {
    margin: 0;
    font-size: 13.5px;
    color: $bp-muted;
  }
}

/* ---------- ปุ่มร่วม ---------- */
.bp-btn {
  min-height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &--ghost {
    border: 1px solid #d8dde5;
    background: $bp-card;
    color: $bp-body;

    &:hover:not(:disabled) {
      background: #f8f9fb;
    }
  }
  &--solid {
    border: 0;
    background: $bp-accent;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: $bp-accent-dark;
    }
  }
  &--grow {
    flex-grow: 1;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.bp-result--pending .bp-btn--ghost {
  border-color: #dcbe83;
  color: #90570a;
}

/* ---------- แท็บเล็ตและแคบกว่า ---------- */
@media (max-width: 1199px) {
  .bp-grid--n4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .bp {
    padding: 24px 24px 32px;
    gap: 18px;
  }
  .bp-head__title {
    font-size: 25px;
  }

  // การ์ดพลิกเป็นแนวนอน: ราคาและปุ่มซ้าย รายการฟีเจอร์ขวา
  .bp-grid--n1,
  .bp-grid--n2,
  .bp-grid--n3,
  .bp-grid--n4 {
    grid-template-columns: minmax(0, 1fr);
  }
  .bp-card {
    grid-template-areas: "lead body" "cta body" "note body";
    grid-template-columns: 240px minmax(0, 1fr);
    grid-template-rows: 1fr auto auto;
    column-gap: 22px;
    padding: 22px 20px;

    &__body {
      margin-top: 0;
      padding: 0 0 0 22px;
      border-top: 0;
      border-left: 1px solid $bp-rule;
      align-self: center;
    }
    &__amount {
      font-size: 30px;
    }
  }
  .bp-cta {
    margin-top: 14px;
  }

  .bp-table {
    min-width: 640px;
  }

  .bp-result {
    flex-wrap: wrap;
  }
  .bp-result__side {
    width: 100%;
  }
}

@media (max-width: 767px) {
  .bp {
    padding: 18px 16px 28px;
  }
  .bp-card {
    grid-template-areas: "lead" "body" "cta" "note";
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto 1fr auto auto;

    &__body {
      margin-top: 20px;
      padding: 18px 0 0;
      border-left: 0;
      border-top: 1px solid $bp-rule;
    }
  }
  .bp-current {
    flex-wrap: wrap;

    &__side {
      align-items: flex-start;
    }
  }
}
</style>

<!--
  b-modal วาดตัวเองไว้ท้าย <body> นอก DOM ของคอมโพเนนต์ scoped style จึงไปไม่ถึง
  บล็อกนี้เลยไม่ scoped และผูกกับ .bp-modal ที่ส่งผ่าน modal-class เท่านั้น
-->
<style lang="scss">
.bp-modal {
  $m-ink: #101828;
  $m-accent: #007bff;
  $m-accent-ink: #0056b3;

  .modal-dialog {
    max-width: 560px;
  }
  .modal-content {
    border: 0;
    border-radius: 18px;
    box-shadow: 0 24px 64px rgba(16, 24, 40, 0.28);
    overflow: hidden;
  }
  .modal-body {
    padding: 0;
  }

  .bp-modal__head {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 24px 26px 18px;
  }
  .bp-modal__title {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 700;
    color: $m-ink;
  }
  .bp-modal__sub {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #8c95a6;
  }
  .bp-modal__x {
    flex-shrink: 0;
    margin-left: auto;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    border: 0;
    background: #f2f4f7;
    color: #667085;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .bp-modal__summary {
    margin: 0 26px;
    padding: 16px 18px;
    background: #f4f9ff;
    border: 1px solid #d6e6fb;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .bp-modal__summary-text {
    flex-grow: 1;
    min-width: 0;
  }
  .bp-modal__plan {
    font-size: 15px;
    font-weight: 600;
    color: $m-ink;
  }
  .bp-modal__summary-price {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }
  .bp-modal__amount {
    font-size: 24px;
    font-weight: 700;
    color: $m-accent-ink;
    font-variant-numeric: tabular-nums;
  }
  .bp-modal__vat {
    font-size: 12px;
    color: #64748b;
  }

  .bp-modal__methods {
    margin: 20px 26px 0;
    padding: 0;
    border: 0;

    legend {
      width: auto;
      padding: 0 0 10px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      color: #8c95a6;
    }
  }
  .bp-method {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 15px 16px;
    margin-bottom: 10px;
    border: 1.5px solid #e3e6eb;
    border-radius: 12px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;

    &.is-on {
      border-color: $m-accent;
      background: #f7fafe;
    }
    input[type="radio"] {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      margin: 2px 0 0;
      accent-color: $m-accent;
    }
  }
  .bp-method__text {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  .bp-method__name {
    font-size: 15px;
    font-weight: 600;
    color: $m-ink;
  }
  .bp-method__detail {
    font-size: 13px;
    line-height: 1.5;
    color: #667085;
  }
  .bp-method__tag {
    flex-shrink: 0;
    padding: 3px 9px;
    border-radius: 999px;
    background: #e8f2ff;
    color: $m-accent-ink;
    font-size: 11px;
    font-weight: 700;
  }

  .bp-modal__safe {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin: 18px 26px 0;
    font-size: 12.5px;
    line-height: 1.5;
    color: #98a2b3;

    svg {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  .bp-modal__foot {
    margin-top: 22px;
    padding: 18px 26px;
    border-top: 1px solid #edf0f4;
    background: #fbfcfd;
    display: flex;
    align-items: center;
    gap: 12px;
  }
}
</style>
